import React from "react";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@agronod/mui-components";
import { CssBaseline, useMediaQuery, useTheme } from "@mui/material";
import { SnackbarProvider, MaterialDesignContent } from "notistack";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { sv } from "date-fns/locale";
import { styled } from "@mui/material/styles";

// Theme
import { figmaTokens } from "@/utils/figmaTokens";
import { agronodThemeOptions } from "@/theme";

// Components
import AppRoutes from "@/components/routing/AppRoutes";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import LoadingProvider from "@/components/common/LoadingProvider";
import RealmRouter from "@/components/auth/RealmRouter";
import RealmGuard from "@/components/auth/RealmGuard";
import RegistrationGuard from "@/components/auth/RegistrationGuard";
import ScrollToTop from "@/components/common/ScrollToTop";
import { AgronodKontoProvider } from "@/contexts/AgronodKontoContext";
import { useUser } from "@/hooks/useUser";
import { isPublicRoute } from "@/constants";

// Custom styled snackbar with modern design
const StyledMaterialDesignContent = styled(MaterialDesignContent)(
  ({ theme }) => ({
    "&.notistack-MuiContent-success": {
      backgroundColor: figmaTokens.status.success.bg,
      border: `1px solid ${theme.palette.success.light}`,
      borderRadius: "16px",
      padding: "16px",
      color: theme.palette.text.primary,
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
      "& #notistack-snackbar": {
        color: theme.palette.text.primary,
        fontWeight: 500,
      },
      "& svg": {
        color: theme.palette.success.main,
      },
    },
    "&.notistack-MuiContent-error": {
      backgroundColor: figmaTokens.status.error.bg,
      border: `1px solid ${theme.palette.error.light}`,
      borderRadius: "16px",
      padding: "16px",
      color: theme.palette.text.primary,
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
      "& #notistack-snackbar": {
        color: theme.palette.text.primary,
        fontWeight: 500,
      },
      "& svg": {
        color: theme.palette.error.main,
      },
    },
    "&.notistack-MuiContent-warning": {
      backgroundColor: figmaTokens.status.warning.bg,
      border: `1px solid ${theme.palette.warning.light}`,
      borderRadius: "16px",
      padding: "16px",
      color: theme.palette.text.primary,
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
      "& #notistack-snackbar": {
        color: theme.palette.text.primary,
        fontWeight: 500,
      },
      "& svg": {
        color: theme.palette.warning.main,
      },
    },
    "&.notistack-MuiContent-info": {
      backgroundColor: figmaTokens.status.info.bg,
      border: `1px solid ${theme.palette.info.light}`,
      borderRadius: "16px",
      padding: "16px",
      color: theme.palette.text.primary,
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
      "& #notistack-snackbar": {
        color: theme.palette.text.primary,
        fontWeight: 500,
      },
      "& svg": {
        color: theme.palette.info.main,
      },
    },
  })
);

// Create a client for React Query
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on 401/403 errors
        if (error?.statusCode === 401 || error?.statusCode === 403) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      staleTime: 10 * 60 * 1000, // 10 minutes - longer cache time
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false, // Disable automatic polling
      refetchIntervalInBackground: false,
      refetchOnMount: true, // Only refetch on mount
      gcTime: 15 * 60 * 1000, // 15 minutes garbage collection time (v5 name)
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Inner bridge: only rendered on authenticated routes, so `useUser()` (which
 * internally calls oidc-react's `useAuth()`) is guaranteed to have an
 * `AuthProvider` in its tree.
 */
const AuthenticatedKontoBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useUser();
  return <AgronodKontoProvider user={user}>{children}</AgronodKontoProvider>;
};

/**
 * Bridge that reads the authenticated user and feeds it into AgronodKontoProvider.
 *
 * On public routes (e.g. `/share/:token`) there is no `AuthProvider` in the
 * tree (see RealmRouter), so we must NOT call `useUser()` — it would throw
 * "AuthProvider context is undefined". In that case we render a konto provider
 * with no user; the public page doesn't need konto scope anyway.
 */
const KontoProviderBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  if (isPublicRoute(location.pathname)) {
    return <AgronodKontoProvider user={undefined}>{children}</AgronodKontoProvider>;
  }
  return <AuthenticatedKontoBridge>{children}</AuthenticatedKontoBridge>;
};

// Wrapper component to use hooks inside ThemeProvider
const AppContent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{
        vertical: "top",
        horizontal: isMobile ? "center" : "right",
      }}
      preventDuplicate
      autoHideDuration={4000}
      dense
      style={{
        marginTop: "24px",
        marginRight: isMobile ? undefined : "24px",
      }}
      Components={{
        success: StyledMaterialDesignContent,
        error: StyledMaterialDesignContent,
        warning: StyledMaterialDesignContent,
        info: StyledMaterialDesignContent,
      }}
    >
      <LoadingProvider>
        <Router>
          <ScrollToTop />
          <RealmRouter>
            <RealmGuard>
              <RegistrationGuard>
                <KontoProviderBridge>
                  <AppRoutes />
                </KontoProviderBridge>
              </RegistrationGuard>
            </RealmGuard>
          </RealmRouter>
        </Router>
      </LoadingProvider>
    </SnackbarProvider>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider options={agronodThemeOptions}>
          <CssBaseline />
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sv}>
            <AppContent />
          </LocalizationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
